import {
  BooleanPropertyConfig,
  DatePropertyConfig,
  Model,
  NumberPropertyConfig,
  ObjectPropertyConfig,
  PropertyType,
  TextPropertyConfig,
} from './models';
import { isNumberStepAligned } from './number-step';

export function ValidatePropertyValue(property: Model<PropertyType>, required: boolean, value: unknown): unknown {
  if (required && (value === undefined || value === null))
    throw new Error(`${property.name} requires a value. Value: ${value}`);
  else if (!required && (value === undefined || value === null)) return value;

  switch (property.type) {
    case PropertyType.BOOLEAN:
      return ValidateBoolean(property, property.config as BooleanPropertyConfig, value);

    case PropertyType.DATE:
      return ValidateDate(property, property.config as DatePropertyConfig, value);

    case PropertyType.NUMBER:
      return ValidateNumber(property, property.config as NumberPropertyConfig, value);

    case PropertyType.OBJECT:
      return ValidateObject(property, property.config as ObjectPropertyConfig, value);

    case PropertyType.TEXT:
      return ValidateText(property, property.config as TextPropertyConfig, value);

    default:
      throw new Error(`${property.type} has no validation code!`);
  }
}
function ValidateBoolean(property: Model<PropertyType>, config: BooleanPropertyConfig, value: unknown): boolean {
  if (typeof value !== 'boolean') {
    throw new Error(`${property.name} must be a boolean. Value: ${value}`);
  }
  if (config.requiredValue !== undefined && config.requiredValue !== null && value !== config.requiredValue) {
    throw new Error(`${property.name} must be ${config.requiredValue}. Value: ${value}`);
  }

  return value;
}

function ValidateDate(property: Model<PropertyType>, config: DatePropertyConfig, value: unknown): Date {
  const valueType = typeof value;
  if (valueType !== 'string' && valueType !== 'number' && valueType !== 'object') {
    throw new Error(`${property.name} must be a date. Value: ${value}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const date = new Date(value as any);
  if (isNaN(date.getTime())) throw new Error(`${property.name} must be a valid date. Value: ${value}`);

  if (config.min) {
    const min = new Date(config.min);
    if (date.getTime() < min.getTime()) {
      throw new Error(`${property.name} must be on or after ${min.toISOString().split('T')[0]}. Value: ${value}`);
    }
  }

  const defaultMax = new Date(Date.UTC(new Date().getFullYear() + 5, 11, 31));
  const max = config.max ? new Date(config.max) : defaultMax;
  const effectiveMax = config.max && max < defaultMax ? max : defaultMax;
  if (date.getTime() > effectiveMax.getTime()) {
    throw new Error(
      `${property.name} must be on or before ${effectiveMax.toISOString().split('T')[0]}. Value: ${value}`
    );
  }

  return date;
}

function ValidateNumber(property: Model<PropertyType>, config: NumberPropertyConfig, _value: unknown): number {
  // Accept complete decimal/exponent strings, never partial parseFloat values or empty strings.
  const value =
    typeof _value === 'string' && /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(_value.trim())
      ? Number(_value)
      : _value;

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${property.name} must be a finite number. Value: ${String(_value)}`);
  }

  if (config.min !== undefined && config.min !== null && value < config.min) {
    throw new Error(`${property.name} must be at least ${config.min}. Value: ${value}`);
  }

  if (config.max !== undefined && config.max !== null && value > config.max) {
    throw new Error(`${property.name} must be at most ${config.max}. Value: ${value}`);
  }

  if (config.step !== undefined && config.step !== null) {
    if (!Number.isFinite(config.step) || config.step <= 0) {
      throw new Error(`${property.name} step must be a finite number greater than zero.`);
    }
    const base = config.min ?? 0;
    if (!isNumberStepAligned(value, config.step, base)) {
      throw new Error(`${property.name} must use increments of ${config.step} starting from ${base}. Value: ${value}`);
    }
  }

  return value;
}

function ValidateObject(property: Model<PropertyType>, _config: ObjectPropertyConfig, value: unknown): unknown {
  //Does not validate object children properties, must do that individually
  if (typeof value !== 'object') {
    throw new Error(`${property.name} must be an object. Value: ${value}`);
  }

  return value;
}

function ValidateText(property: Model<PropertyType>, config: TextPropertyConfig, value: unknown): string {
  if (typeof value !== 'string') {
    throw new Error(`${property.name} must be a string. Value: ${value}`);
  }

  if (config.allowedValues?.length && !config.allowedValues.includes(value)) {
    throw new Error(`${property.name} must be the following: ${config.allowedValues.join(', ')} Value: ${value}`);
  }

  if (config.minLength !== undefined && config.minLength !== null && value.length < config.minLength) {
    throw new Error(`${property.name} must have at least ${config.minLength} characters. Value: ${value}`);
  }

  if (config.maxLength !== undefined && config.maxLength !== null && value.length > config.maxLength) {
    throw new Error(`${property.name} must have no more than ${config.maxLength} characters. Value: ${value}`);
  }

  return value;
}
